import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/i18n/LangContext";
import { ROUTES, LANGS } from "@/i18n/routes";
import { SiteProvider } from "@/context/SiteContext";
import { UIProvider } from "@/context/UIContext";
import { SmoothScroll } from "@/components/layout/SmoothScroll";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { MobileBar } from "@/components/layout/MobileBar";
import { CookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home";

const Rooms = lazy(() => import("@/pages/Rooms"));
const RoomDetail = lazy(() => import("@/pages/RoomDetail"));
const Services = lazy(() => import("@/pages/Services"));
const Corporate = lazy(() => import("@/pages/Corporate"));
const MeetingRooms = lazy(() => import("@/pages/MeetingRooms"));
const MeetingRoomDetail = lazy(() => import("@/pages/MeetingRoomDetail"));
const Events = lazy(() => import("@/pages/Events"));
const EventType = lazy(() => import("@/pages/EventType"));
const Restaurant = lazy(() => import("@/pages/Restaurant"));
const Location = lazy(() => import("@/pages/Location"));
const Biosphere = lazy(() => import("@/pages/Biosphere"));
const SustainabilitySurvey = lazy(() => import("@/pages/SustainabilitySurvey"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const Landing = lazy(() => import("@/pages/Landing"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminPanel = lazy(() => import("@/pages/admin/AdminPanel"));

function PublicLayout() {
  return (
    <SmoothScroll>
      <div className="grain min-h-screen flex flex-col">
        <Header />
        <div className="flex-1">
          <Suspense fallback={<div className="min-h-[60vh] bg-sand" />}>
            <Outlet />
          </Suspense>
        </div>
        <Footer />
        <MobileBar />
        <CookieBanner />
      </div>
    </SmoothScroll>
  );
}

const PAGES = {
  home: Home,
  rooms: Rooms,
  roomDetail: RoomDetail,
  services: Services,
  corporate: Corporate,
  meetingRooms: MeetingRooms,
  meetingRoomDetail: MeetingRoomDetail,
  events: Events,
  weddings: () => <EventType type="weddings" />,
  celebrations: () => <EventType type="celebrations" />,
  groups: () => <EventType type="groups" />,
  restaurant: Restaurant,
  location: Location,
  biosphere: Biosphere,
  survey: SustainabilitySurvey,
  gallery: Gallery,
  blog: Blog,
  blogPost: BlogPost,
  contact: Contact,
  legal: () => <Legal kind="legal" />,
  privacy: () => <Legal kind="privacy" />,
  cookies: () => <Legal kind="cookies" />,
  nearSeat: () => <Landing type="nearSeat" />,
  nearMontserrat: () => <Landing type="nearMontserrat" />,
  parkingBarcelona: () => <Landing type="parkingBarcelona" />,
  weddingsBaix: () => <Landing type="weddingsBaix" />,
};

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <SiteProvider>
          <UIProvider>
            <Routes>
              <Route element={<PublicLayout />}>
                {Object.entries(PAGES).flatMap(([key, Page]) =>
                  LANGS.map((lang) => <Route key={`${key}-${lang}`} path={ROUTES[key][lang]} element={<Page />} />)
                )}
                <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
              </Route>
              <Route path="/admin" element={<Suspense fallback={null}><AdminLogin /></Suspense>} />
              <Route path="/admin/panel" element={<Suspense fallback={null}><AdminPanel /></Suspense>} />
            </Routes>
            <Toaster position="bottom-right" richColors closeButton />
          </UIProvider>
        </SiteProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
